import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const RANGES = [
    { label: "7d", days: 7 },
    { label: "14d", days: 14 },
    { label: "30d", days: 30 },
];

export default function AdminAnalyticsPage() {
    const [days, setDays] = useState(7);
    const [summary, setSummary] = useState([]);
    const [topTiles, setTopTiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, t] = await Promise.all([
                api.get(`/admin/analytics/summary?days=${days}`),
                api.get(`/admin/analytics/top-tiles?days=${days}&limit=10`),
            ]);
            setSummary(s.data.items || []);
            setTopTiles(t.data.items || []);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally { setLoading(false); }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const total = useMemo(() => summary.reduce((n, r) => n + (r.count || 0), 0), [summary]);

    return (
        <div className="space-y-6" data-testid="admin-analytics-page">
            <header>
                <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Growth</div>
                <h1 className="mt-1 font-heading text-3xl font-extrabold text-[var(--text-primary)] sm:text-4xl">Analytics</h1>
                <p className="mt-1 font-body text-sm text-[var(--text-muted)]">
                    Customer-side event counts by type · {total.toLocaleString()} events in the last {days} days.
                </p>
            </header>

            <div className="flex flex-wrap items-center gap-2" data-testid="analytics-range-chips">
                {RANGES.map((r) => (
                    <Button
                        key={r.days}
                        onClick={() => setDays(r.days)}
                        variant={days === r.days ? "default" : "outline"}
                        className={days === r.days ? "btn-primary rounded-full" : "rounded-full border-slate-200"}
                        data-testid={`analytics-range-${r.days}`}
                    >
                        {r.label}
                    </Button>
                ))}
            </div>

            <div className="card-soft" data-testid="analytics-bar-card">
                <h2 className="font-heading text-lg font-bold text-[var(--text-primary)]">Events by type</h2>
                <p className="font-body text-xs text-[var(--text-muted)]">Last {days} days</p>
                <div className="mt-4 h-72">
                    {summary.length === 0 && !loading ? (
                        <div className="flex h-full items-center justify-center text-sm text-[var(--text-muted)]">
                            No events recorded in the last {days} days.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={summary} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="event_type" angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <Tooltip cursor={{ fill: "#FFF3E6" }} />
                                <Bar dataKey="count" fill="#FF6B00" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <div className="card-soft" data-testid="analytics-top-tiles-card">
                <h2 className="font-heading text-lg font-bold text-[var(--text-primary)]">Top tiles</h2>
                <p className="font-body text-xs text-[var(--text-muted)]">Most-tapped home tiles</p>
                <div className="mt-3 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-[var(--border-soft)] text-left">
                                <th className="py-2 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">#</th>
                                <th className="py-2 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Tile</th>
                                <th className="py-2 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Slug</th>
                                <th className="py-2 text-right font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Taps</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topTiles.length === 0 && !loading && (
                                <tr><td colSpan={4} className="py-6 text-center text-sm text-[var(--text-muted)]">No tile taps yet.</td></tr>
                            )}
                            {topTiles.map((r, i) => (
                                <tr key={r.target_id || i} className="border-b border-[var(--border-soft)]" data-testid={`analytics-tile-row-${i}`}>
                                    <td className="py-2 font-body text-sm text-[var(--text-muted)]">{i + 1}</td>
                                    <td className="py-2 font-heading text-sm font-semibold text-[var(--text-primary)]">{r.target_label || "—"}</td>
                                    <td className="py-2 font-body text-xs text-[var(--text-muted)]">{r.target_id || "—"}</td>
                                    <td className="py-2 text-right font-heading text-sm font-bold text-[var(--brand-orange)]">{r.count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
